import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import ItemCreatePage from "./ItemCreatePage";
import ItemDetailPage from "./ItemDetailPage";
import BoardTabs from "../../components/lost/BoardTabs";
import ItemCard from "../../components/lost/ItemCard";
import BackButton from "../../components/common/BackButton";
import { supabase } from "../../lib/supabaseClient";
import "./Board.css";

const BoardPage = () => {
  const location = useLocation();

  const [boardType, setBoardType] = useState("all");
  const [isCreating, setIsCreating] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(
    location.state?.openItemId ?? null
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadingRef = useRef(false);

  const fetchItems = useCallback(
    async (currentBoardType, silent = false) => {
      if (loadingRef.current) return;

      loadingRef.current = true;

      if (!silent) {
        setLoading(true);
      }

      try {
        let query = supabase
          .from("lost_items")
          .select("*")
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false });

        if (currentBoardType === "lost") {
          query = query.eq("item_type", "분실").neq("status", "완료");
        }

        if (currentBoardType === "found") {
          query = query.eq("item_type", "습득").neq("status", "완료");
        }

        if (currentBoardType === "completed") {
          query = query.eq("status", "완료");
        }

        const { data, error } = await query;

        if (error) throw error;

        setItems(data || []);
      } catch (error) {
        console.error("데이터 로딩 에러:", error.message);
      } finally {
        loadingRef.current = false;

        if (!silent) {
          setLoading(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    if (!isCreating) {
      setItems([]);
      fetchItems(boardType);
    }
  }, [boardType, isCreating, fetchItems]);

  // 3초마다 게시글 조용히 새로고침
  useEffect(() => {
    if (isCreating) return;

    const interval = setInterval(() => {
      fetchItems(boardType, true);
    }, 3000);

    return () => clearInterval(interval);
  }, [boardType, isCreating, fetchItems]);

  const handleCreateClick = async () => {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        alert("로그인이 필요한 서비스입니다. 먼저 로그인을 진행해 주세요!");
        return;
      }

      setSelectedItemId(null);
      setIsCreating(true);
    } catch (error) {
      console.error("로그인 확인 에러:", error.message);
      alert("로그인이 필요한 서비스입니다.");
    }
  };

  const handleModalClose = () => {
    setSelectedItemId(null);
  };

  const handleGoBackFromCreate = () => {
    setIsCreating(false);
    setSelectedItemId(null);
  };

  if (isCreating) {
    return <ItemCreatePage onGoBack={handleGoBackFromCreate} />;
  }

  return (
    <div className="board-container">
      <BackButton />

      <div className="board-header">
        <h2>
          {boardType === "all" && "전체 게시판"}
          {boardType === "lost" && "분실물 게시판"}
          {boardType === "found" && "습득물 게시판"}
          {boardType === "completed" && "주인찾음 게시판"}
        </h2>

        <button className="add-button" onClick={handleCreateClick}>
          + 분실물 추가
        </button>
      </div>

      <BoardTabs boardType={boardType} setBoardType={setBoardType} />

      <div className="item-grid">
        {items.map((item) => (
          <ItemCard
            key={item.id}
            item={item}
            onClick={() => setSelectedItemId(item.id)}
          />
        ))}
      </div>

      {loading && (
        <div className="loading-spinner">
          데이터를 불러오는 중입니다...
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="no-more-data">
          모든 게시글을 다 읽으셨습니다!
        </div>
      )}

      {!isCreating && selectedItemId && (
        <ItemDetailPage itemId={selectedItemId} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default BoardPage;